import { computed } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeAllEntities, setEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { SortParams } from 'mbs-ui-kit/models/sort';
import { PagingParams, QueryFiltersParams, QueryPagingParams, getPagingParams } from 'mbs-ui-shared@models';
import { isDataObsolete } from 'mbs-ui-shared@utils/store';
import moment from 'moment';
import { EMPTY, Observable, pipe } from 'rxjs';
import { mergeMap, switchMap, take, tap } from 'rxjs/operators';
import {
  DataSliceState,
  PagingSignalState,
  PagingSliceState,
  WithPagingAndDataConfig,
  createInitialPagingSignalState,
  initialDataState,
  initialPagingSliceState
} from './withPagingAndData.types';

// eslint-disable-next-line @typescript-eslint/ban-types
export function withPagingAndData<T extends object, F extends object = Record<string, never>>(config: WithPagingAndDataConfig<T, F>) {
  const { selectId } = config;

  return signalStoreFeature(
    withEntities<T>(),
    withState<PagingSignalState<F>>(createInitialPagingSignalState<F>(config.initialCustomState)),

    // -----------------------------------------------------------------------
    // Computed signals (replaces selectors)
    // -----------------------------------------------------------------------
    withComputed((store) => ({
      selectAll: computed(() => store.entities()),

      selectFirstEntity: computed(() => store.entities()[0]),

      selectLoading: computed(() => Object.values(store.loadingById()).some(Boolean)),

      // data
      selectDataLoading: computed(() => store.data().loading),
      selectDataLoaded: computed(() => store.data().loaded),
      selectDataTotal: computed(() => store.data().total),
      selectData: computed(() =>
        store
          .data()
          .entityIds.map((id) => store.entityMap()[id])
          .filter(Boolean)
      ),
      selectDataCount: computed(() => store.data().entityIds.length),
      selectDataHasError: computed(() => store.data().isErrorState),
      selectAllDataLoaded: computed(() => store.data().loaded && store.data().total <= store.data().entityIds.length),

      // paging
      selectPagingLoading: computed(() => store.paging().loading),
      selectPagingLoaded: computed(() => store.paging().loaded),
      selectPagingCurrentPageNumber: computed(() => store.paging().currentPageNumber),
      selectPagingPerPage: computed(() => store.paging().perPage),
      selectPagingTotal: computed(() => store.paging().total),
      selectPagingFilters: computed(() => store.paging().filters),
      selectPagingHasError: computed(() => store.paging().isErrorState),
      selectPagingPageNumbers: computed(() => Object.keys(store.paging().pages).map(Number)),
      selectCurrentPage: computed(() => {
        const pagingState = store.paging();
        return (pagingState.pages[pagingState.currentPageNumber] ?? []).map((id) => store.entityMap()[id]).filter(Boolean);
      })
    })),

    // Combined computed (depend on the computed above)
    withComputed((store) => ({
      selectDataEntityLoading: computed(() => store.selectLoading() || store.selectDataLoading()),
      selectPagingEntityLoading: computed(() => store.selectLoading() || store.selectPagingLoading()),
      selectSortSettings: computed(() => store.paging().sort)
    })),

    withMethods((store) => {
      // Resolve service functions inside injection context
      const getEntityById = config.getEntityById?.() ?? null;
      const getEntitiesByIds = config.getEntitiesByIds?.() ?? null;
      const getEntities = config.getEntities();

      const upsertEntities = (entities: T[]) => setEntities(entities, { selectId });

      return {
        entityById: (id: string) => computed(() => store.entityMap()[id]),

        entitiesById: (ids: string[]) => computed(() => ids.map((id) => store.entityMap()[id]).filter(Boolean)),

        // Returns only entities that were fetched via loadEntityById (have loadedById flag)
        loadedEntityById: (id: string) => computed(() => (store.loadedById()[id] ? store.entityMap()[id] : undefined)),

        loadedEntitiesById: (ids: string[]) =>
          computed(() => ids.filter((id) => store.loadedById()[id]).map((id) => store.entityMap()[id])),

        loadingForId: (id: string) => computed(() => !!store.loadingById()[id]),

        missedById: (id: string) => computed(() => store.missedIds().includes(id)),

        missedByIds: (ids: string[]) => computed(() => ids.filter((id) => store.missedIds().includes(id))),

        // Returns the ID of an entity using the configured selectId
        entityId: (entity: T) => selectId(entity),

        setEntities: (entities: T[]): void => {
          patchState(store, upsertEntities(entities));
        },

        clearEntities: (): void => {
          patchState(store, removeAllEntities());
        },

        clearData: (): void => {
          patchState(store, { data: { ...initialDataState } });
        },

        clearPages: (): void => {
          patchState(store, (state) => ({ paging: clearPagingState(state.paging, true) }));
        },

        clearAll: (): void => {
          patchState(store, removeAllEntities(), { data: { ...initialDataState } }, (state) => ({
            paging: clearPagingState(state.paging, true)
          }));
        },

        // -------------------------------------------------------------------
        // Load single entity by ID
        // -------------------------------------------------------------------
        loadEntityById: rxMethod<{ id: string; force?: boolean; quiet?: boolean }>(
          pipe(
            mergeMap(({ id, force, quiet }) => {
              if (!id || (!force && !!store.entityMap()[id])) return EMPTY;

              !quiet && patchState(store, (state) => ({ loadingById: { ...state.loadingById, [id]: true } }));

              if (!getEntityById) return EMPTY;

              return getEntityById(id).pipe(
                tapResponse({
                  next: (entity) => {
                    patchState(store, upsertEntities([entity]), (state) => ({
                      loadedById: { ...state.loadedById, [id]: true },
                      loadingById: { ...state.loadingById, [id]: false }
                    }));
                  },
                  error: () => patchState(store, (state) => ({ loadingById: { ...state.loadingById, [id]: false } }))
                })
              );
            })
          )
        ),

        // -------------------------------------------------------------------
        // Load multiple entities by IDs
        // -------------------------------------------------------------------
        loadEntitiesByIds: rxMethod<{ ids: string[]; force?: boolean; quiet?: boolean }>(
          pipe(
            mergeMap(({ ids, force, quiet }) => {
              if (!ids?.length) return EMPTY;

              const missingIds = force ? ids : ids.filter((id) => !store.entityMap()[id]);

              if (!missingIds.length) return EMPTY;

              !quiet &&
                patchState(store, (state) => ({
                  loadingById: { ...state.loadingById, ...Object.fromEntries(missingIds.map((id) => [id, true])) }
                }));

              if (!getEntitiesByIds) return EMPTY;

              return getEntitiesByIds(missingIds).pipe(
                tapResponse({
                  next: (entities) => {
                    const receivedIds = (entities ?? []).map((entity) => selectId(entity));
                    patchState(store, upsertEntities(entities ?? []), (state) => ({
                      loadingById: { ...state.loadingById, ...Object.fromEntries(missingIds.map((id) => [id, false])) },
                      loadedById: { ...state.loadedById, ...Object.fromEntries(missingIds.map((id) => [id, true])) },
                      missedIds: [
                        ...state.missedIds,
                        ...missingIds.filter((id) => !state.missedIds.includes(id) && !receivedIds.includes(id))
                      ]
                    }));
                  },
                  error: () =>
                    patchState(store, (state) => ({
                      loadingById: { ...state.loadingById, ...Object.fromEntries(missingIds.map((id) => [id, false])) }
                    }))
                })
              );
            })
          )
        ),

        // -------------------------------------------------------------------
        // Load "data" (all entities, infinite scroll / load-more pattern)
        // -------------------------------------------------------------------
        loadData: rxMethod<{ isFullRefresh?: boolean; allowLoadMore?: boolean; limit?: number } | undefined>(
          pipe(
            mergeMap((rawAction) => {
              const action = rawAction ?? {};
              const isFullRefresh = action.isFullRefresh ?? false;
              const allowLoadMore = action.allowLoadMore ?? false;
              const limit = action.limit ?? null;
              const data = store.data();
              const paging = store.paging();
              const isPagingOutdated = isDataObsolete(paging.obsolescenceMark);

              let newData: DataSliceState = { ...data };
              let dataChanged = false; // flag to indicate if we need to update data slice

              // prepare data slice for loading based on current state, action, and paging slice (for potential cross-copy)
              const isDataOutdated = isDataObsolete(data.obsolescenceMark);
              let allLoaded = data.loaded && data.total <= data.entityIds.length;
              if (isDataOutdated || isFullRefresh || !data.loaded || (!allLoaded && allowLoadMore)) {
                if (isFullRefresh || isDataOutdated) {
                  dataChanged = true;
                  newData = { ...initialDataState };
                }

                // Cross-copy from paging page 1 if eligible
                if (
                  !isFullRefresh &&
                  !isPagingOutdated &&
                  !newData.loaded &&
                  paging.loaded &&
                  isEmpty(paging.filters) &&
                  paging.pages[1] !== undefined
                ) {
                  dataChanged = true;
                  newData = {
                    ...newData,
                    entityIds: [...paging.pages[1]],
                    total: paging.total,
                    loaded: true,
                    isErrorState: false,
                    obsolescenceMark: paging.obsolescenceMark
                  };
                }

                newData.loading =
                  (!newData.loaded || (newData.total > newData.entityIds.length && allowLoadMore)) &&
                  (!newData.isErrorState || isFullRefresh);
              }

              // effect
              allLoaded = data.loaded && data.total <= data.entityIds.length;
              const shouldFetch = (!newData.loaded || (!allLoaded && allowLoadMore)) && (!newData.isErrorState || isFullRefresh);

              if (shouldFetch) {
                newData.loading = true;
                dataChanged = true;
              }

              dataChanged && patchState(store, { data: newData });

              if (!shouldFetch) return EMPTY;

              const offset = newData.entityIds.length;

              return getEntities({ offset, ...(limit > 0 ? { limit } : {}) }).pipe(
                tapResponse({
                  next: (result) => {
                    const resultIds = result.data.map((entity) => selectId(entity));
                    patchState(store, upsertEntities(result.data), (state) => ({
                      data: {
                        ...state.data,
                        total: result.total,
                        loaded: true,
                        loading: false,
                        isErrorState: false,
                        entityIds: isFullRefresh || !allowLoadMore ? resultIds : [...state.data.entityIds, ...resultIds],
                        obsolescenceMark: state.data.obsolescenceMark ?? moment().valueOf()
                      }
                    }));
                  },
                  error: () => patchState(store, (state) => ({ data: { ...state.data, loading: false, isErrorState: true } }))
                })
              );
            })
          )
        ),

        // -------------------------------------------------------------------
        // Load a specific page (paging pattern)
        // -------------------------------------------------------------------
        loadPage: rxMethod<{ filters?: QueryFiltersParams; pageNumber?: number; perPage?: number; isFullRefresh?: boolean }>(
          pipe(
            switchMap((rawAction) => {
              const action = rawAction ?? {};
              const isFullRefresh = action.isFullRefresh ?? false;
              const filters = action.filters ?? null;
              const pageNumber = action.pageNumber ?? null;
              const perPage = action.perPage ?? null;
              const data = store.data();
              const paging = store.paging();
              const isDataOutdated = isDataObsolete(data.obsolescenceMark);
              const isPagingOutdated = isDataObsolete(paging.obsolescenceMark);

              let newPaging: PagingSliceState = { ...paging };
              let pagingChanged = false; // flag to indicate if we need to update paging slice
              let needsClear = false; // flag to indicate if we need to clear paging page cache (pages)

              // prepare paging slice for loading based on current state, action, and data slice (for potential cross-copy)

              // Update page number
              if (pageNumber && pageNumber !== newPaging.currentPageNumber) {
                pagingChanged = true;
                newPaging.currentPageNumber = pageNumber;
              }

              if (isPagingOutdated || isFullRefresh) {
                needsClear = true;
              }

              // Filter change
              if (filters != null && !isEqual(paging.filters, filters)) {
                newPaging.filters = cloneDeep(filters);
                newPaging.currentPageNumber = initialPagingSliceState.currentPageNumber;
                pagingChanged = true;
                needsClear = true;
              }

              // Per-page change
              if (perPage && paging.perPage !== perPage) {
                newPaging.perPage = perPage;
                newPaging.currentPageNumber = initialPagingSliceState.currentPageNumber;
                pagingChanged = true;
                needsClear = true;
              }

              if (needsClear) {
                newPaging = clearPagingState(newPaging, false);
                pagingChanged = true;
              }

              // Cross-copy from data to paging page 1 if eligible
              if (
                !isFullRefresh &&
                !isDataOutdated &&
                data.loaded &&
                isEmpty(newPaging.filters) &&
                newPaging.currentPageNumber === initialPagingSliceState.currentPageNumber &&
                !(1 in newPaging.pages) &&
                newPaging.perPage &&
                (data.entityIds.length >= newPaging.perPage || (data.total < newPaging.perPage && data.entityIds.length === data.total))
              ) {
                newPaging = {
                  ...newPaging,
                  pages: { ...newPaging.pages, [1]: data.entityIds.slice(0, newPaging.perPage) },
                  total: data.total,
                  loaded: true,
                  isErrorState: false,
                  obsolescenceMark: newPaging.obsolescenceMark
                    ? Math.min(newPaging.obsolescenceMark, data.obsolescenceMark)
                    : data.obsolescenceMark
                };
              }

              // effect
              const alreadyLoaded = newPaging.loaded && !!newPaging.pages[newPaging.currentPageNumber];
              const shouldFetch = !alreadyLoaded && (!newPaging.isErrorState || isFullRefresh);

              if (shouldFetch) {
                newPaging.loading = true;
                pagingChanged = true;
              }

              pagingChanged && patchState(store, { paging: newPaging });

              if (!shouldFetch) return EMPTY;

              const pagingParams: PagingParams = { currentPageNumber: newPaging.currentPageNumber, perPage: newPaging.perPage };

              return getEntities({ ...newPaging.filters, ...getPagingParams(pagingParams) } as QueryFiltersParams & QueryPagingParams).pipe(
                tapResponse({
                  next: (result) => {
                    const resultIds = result.data.map((entity) => selectId(entity));
                    const pageNumber = newPaging.currentPageNumber;

                    patchState(store, upsertEntities(result.data), (state) => {
                      const updatedPaging: PagingSliceState = {
                        ...state.paging,
                        pages: { ...state.paging.pages, [pageNumber]: resultIds },
                        total: result.total,
                        loading: false,
                        loaded: true,
                        isErrorState: false,
                        obsolescenceMark: state.paging.obsolescenceMark ?? moment().valueOf()
                      };

                      // Cross-copy to data if page 1 with no filters
                      const updatedData =
                        isEmpty(updatedPaging.filters) &&
                        pageNumber === initialPagingSliceState.currentPageNumber &&
                        (!state.data.loaded || result.data.length > state.data.entityIds.length)
                          ? {
                              ...state.data,
                              entityIds: resultIds,
                              total: result.total,
                              loaded: true,
                              isErrorState: false,
                              obsolescenceMark: updatedPaging.obsolescenceMark
                            }
                          : state.data;

                      return { paging: updatedPaging, data: updatedData };
                    });
                    config.onPageLoaded?.(result, (updater) => patchState(store, (s) => ({ custom: updater(s.custom) })));
                  },
                  error: () => patchState(store, (state) => ({ paging: { ...state.paging, loading: false, isErrorState: true } }))
                })
              );
            })
          )
        ),

        // -------------------------------------------------------------------
        // Refresh data + paging in a single API call
        // -------------------------------------------------------------------
        refreshDataAndPaging: rxMethod<void>(
          pipe(
            tap(() => {
              patchState(store, (state) => ({
                data: { ...initialDataState, loading: true },
                paging: { ...clearPagingState(state.paging, true), loading: true }
              }));
            }),
            mergeMap(() =>
              getEntities({ offset: 0 }).pipe(
                tapResponse({
                  next: (result) => {
                    const resultIds = result.data.map((entity) => selectId(entity));
                    const obsolescenceMark = moment().valueOf();

                    patchState(store, upsertEntities(result.data), (state) => ({
                      data: {
                        ...state.data,
                        total: result.total,
                        loaded: true,
                        loading: false,
                        isErrorState: false,
                        entityIds: resultIds,
                        obsolescenceMark
                      },
                      paging: {
                        ...state.paging,
                        pages: { ...state.paging.pages, [1]: resultIds.slice(0, state.paging.perPage ?? resultIds.length) },
                        total: result.total,
                        loading: false,
                        loaded: true,
                        isErrorState: false,
                        obsolescenceMark
                      }
                    }));
                  },
                  error: () =>
                    patchState(store, (state) => ({
                      data: { ...state.data, loading: false, isErrorState: true },
                      paging: { ...state.paging, loading: false, isErrorState: true }
                    }))
                })
              )
            )
          )
        ),

        // -------------------------------------------------------------------
        // Direct fetch + store update (returns Observable for caller)
        // -------------------------------------------------------------------
        directGetEntityById: getEntityById
          ? (id: string): Observable<T> =>
              getEntityById(id).pipe(
                tap((entity) =>
                  patchState(store, upsertEntities([entity]), (state) => ({
                    loadedById: { ...state.loadedById, [id]: true }
                  }))
                ),
                take(1)
              )
          : null,

        directGetEntitiesByIds: getEntitiesByIds
          ? (ids: string[]): Observable<T[]> =>
              getEntitiesByIds(ids).pipe(
                tap((entities) =>
                  patchState(store, upsertEntities(entities ?? []), (state) => ({
                    loadedById: { ...state.loadedById, ...entities.reduce((acc, entity) => ({ ...acc, [selectId(entity)]: true }), {}) }
                  }))
                ),
                take(1)
              )
          : null
      };
    }),

    // -----------------------------------------------------------------------
    // Secondary methods (depend on primary methods via store reference)
    // -----------------------------------------------------------------------
    withMethods((store) => ({
      setPageNumber: (pageNumber: number): void => {
        store.loadPage({ pageNumber });
      },

      setPerPage: (perPage: number): void => {
        store.loadPage({ perPage });
      },

      setSortSettings: (sort: SortParams): void => {
        patchState(store, (state) => ({ paging: { ...state.paging, sort } }));
      }
    }))
  );
}

function clearPagingState(current: PagingSliceState, clearCurrentPage = false): PagingSliceState {
  return {
    ...current,
    pages: {},
    total: initialPagingSliceState.total,
    loaded: initialPagingSliceState.loaded,
    isErrorState: initialPagingSliceState.isErrorState,
    obsolescenceMark: initialPagingSliceState.obsolescenceMark,
    loading: initialPagingSliceState.loading,
    ...(clearCurrentPage ? { currentPageNumber: initialPagingSliceState.currentPageNumber } : {})
  };
}
